import { portalApi } from "./client";
import type { PortalBatchResult } from "./batch";

// TPS Servicing Portal PR-G — portal-side client + DTO types for the firm's
// "Distribution Ledger". Mirrors the C# records the backend exposes under
// /api/portal/tps/distributions. Kept inside the portal bundle so nothing here
// imports staff types. No React Query in the portal — callers load with
// useState and re-run the loader after approve.

export interface TpsDistribution {
  id: string;
  loanId: string;
  loanNumber: string;
  paymentId: string;
  /** DateOnly "YYYY-MM-DD". */
  periodStart: string;
  totalAmount: number;
  status: string;
  canApprove: boolean;
  blockReason: string | null;
  fundsClearedAt: string | null;
  approvedAt: string | null;
  paidAt: string | null;
  reversedAt: string | null;
  payeeCount: number;
  /** Distribution Gate PR-3 — the latest TPS return note when the payout was
   *  Returned for correction (the mirror of the newest thread comment). */
  returnedReason: string | null;
}

/** Distribution Gate PR-3 (D-14) — one entry of the payout's return
 *  conversation with Phoenix (keyed directly on the distribution, which never
 *  supersedes). Phoenix entries carry the neutral "Phoenix Capital" label —
 *  staff names never reach the portal. */
export interface TpsDistributionComment {
  id: string;
  authorRole: "phoenix" | "servicer";
  authorName: string;
  body: string;
  createdAt: string;
}

export interface TpsDistributionLine {
  recipientCounterpartyId: string;
  recipientName: string;
  recipientKind: string;
  amount: number;
  destination: string;
  accountStatus: string;
  transferStatus: string | null;
  stripeTransferId: string | null;
}

export interface TpsDistributionDetail {
  summary: TpsDistribution;
  lines: TpsDistributionLine[];
  /** Distribution Gate PR-3 — the return-for-correction thread, oldest first
   *  (null when there's been no return round). */
  comments: TpsDistributionComment[] | null;
}

/** Read-only dashboard counts (audit F-08/F-31). `pendingCount` includes
 *  payments that would materialize as Pending on the next list read;
 *  `approvableCount` applies the same per-row cleared-funds math as
 *  `canApprove` — never "pending minus hasBlockReason" (a blockReason is only
 *  stamped on a FAILED approve attempt). */
export interface TpsDistributionsSummary {
  pendingCount: number;
  approvableCount: number;
  blockedCount: number;
}

export const tpsDistributionsApi = {
  list: () => portalApi.get<TpsDistribution[]>("/tps/distributions"),
  /** The Home tile read — unlike `list` this NEVER lazily materializes rows,
   *  so a dashboard render never writes (F-31). */
  summary: () => portalApi.get<TpsDistributionsSummary>("/tps/distributions/summary"),
  get: (distributionId: string) =>
    portalApi.get<TpsDistributionDetail>(`/tps/distributions/${distributionId}`),
  /** Distribution Gate PR-3 (D-14) — return a pending payout to Phoenix for
   *  correction with a required note. Flips it to Returned and records the note
   *  on the thread; no money moves. */
  return: (distributionId: string, note: string) =>
    portalApi.post<TpsDistributionDetail>(`/tps/distributions/${distributionId}/return`, { note }),
  /** Approve + pay out a distribution. Blocked (400) when funds aren't cleared
   *  or a payee isn't onboarded; the reason rides the error AND the refreshed
   *  `blockReason` on the distribution. */
  approve: (distributionId: string) =>
    portalApi.post<TpsDistributionDetail>(`/tps/distributions/${distributionId}/approve`),
  /** SP-2b — approve + pay out up to 50 distributions in one call. Per-id
   *  results; a blocked/failed distribution reports its reason per id. */
  batchApprove: (ids: string[]) =>
    portalApi.post<PortalBatchResult>("/tps/distributions/batch-approve", { ids }),
};
